<template>
  <h2
    class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
    style="color: var(--text-color)"
  >
    Referrals
  </h2>
  <div>
    <Card>
      <template #header>
        <div
          class="flex flex-wrap justify-content-between align-items-center tw-p-2 tw-gap-2"
        >
          <div class="flex flex-wrap tw-gap-2">
            <Button
              icon="pi pi-link"
              class="p-button-sm p-button-outlined tw-w-fit tw-flex tw-items-center tw-gap-1.5 tw-h-[48px]"
              @click="getAffiliateLink(podcaster.podcaster_id)"
            >
              <i class="pi pi-link" />
              Copy your referral link
            </Button>
          </div>
          <InputText
            class="w-full sm:w-auto"
            type="text"
            v-model="referralsTableFilter.global.value"
            placeholder="Global Search"
          />
        </div>
      </template>
      <template #content>
        <DataTable
          :value="referrals"
          :paginator="true"
          :rows="10"
          :first="initFirst"
          @page="onChangePage"
          :filters="referralsTableFilter"
          class="p-datatable-sm"
          :stripedRows="true"
          :globalFilterFields="['id', 'name', 'email', 'created_at']"
        >
          <Column field="id" header="ID" :sortable="true" style="width: 10%">
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.id">
                {{ slotProps.data.id }}
              </div>
            </template>
          </Column>
          <!-- Name -->
          <Column
            field="name"
            header="Name"
            :sortable="true"
            style="width: 20%"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.name">
                {{ slotProps.data.name }}
              </div>
            </template>
          </Column>
          <!-- Email -->
          <Column
            field="email"
            header="Email"
            :sortable="true"
            style="width: 30%"
          >
            <template #body="slotProps">
              <div class="truncate-text" :title="slotProps.data.email">
                {{ slotProps.data.email }}
              </div>
            </template>
          </Column>
          <!-- Join Date -->
          <Column
            field="created_at"
            header="Join Date"
            :sortable="true"
            style="width: 30%"
          >
            <template #body="slotProps">
              <div class="date-cell" :title="slotProps.data.created_at">
                {{
                  new Date(slotProps.data.created_at)
                    .toISOString()
                    .split("T")[0]
                }}
              </div>
            </template>
          </Column>
          <!-- Actions -->
          <Column header="Gifts" style="width: 15%">
            <template #body="slotProps">
              <div class="tw-flex tw-gap-1">
                <!-- Open Gifts Button -->
                <Button
                  icon="pi pi-gift"
                  :class="['p-button-sm', 'p-button-outlined']"
                  @click="openGiftsModal(slotProps.data)"
                  :title="
                    slotProps.data.gifts && slotProps.data.gifts.length > 0
                      ? `View ${slotProps.data.gifts.length} gift(s)`
                      : 'No gifts'
                  "
                />
              </div>
            </template>
          </Column>
          <template #paginatorstart>
            <Button
              type="button"
              icon="pi pi-refresh"
              @click="fetchReferrals(true)"
              text
            />
          </template>
        </DataTable>
      </template>
    </Card>

    <!-- Enhanced Gifts Modal -->
    <Dialog
      v-model:visible="showGiftsModal"
      :header="`${selectedReferralName} - Gifts`"
      modal
      :style="{ width: '500px', maxWidth: '90vw' }"
      class="gifts-modal"
    >
      <div class="gifts-modal-content">
        <!-- Empty State -->
        <div
          v-if="!selectedReferral?.gifts?.length"
          class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-12 tw-text-center"
          style="background: var(--surface-card)"
        >
          <div class="tw-mb-6 tw-opacity-60">
            <i
              class="pi pi-gift tw-text-5xl"
              style="color: var(--surface-400)"
            ></i>
          </div>
          <h4
            class="tw-m-0 tw-mb-2 tw-text-xl tw-font-semibold"
            style="color: var(--text-color)"
          >
            No Gifts Yet
          </h4>
          <p
            class="tw-m-0 tw-text-base tw-leading-6"
            style="color: var(--text-color-secondary)"
          >
            This referral hasn't received any gifts yet.
          </p>
        </div>

        <!-- Gifts List -->
        <div v-else style="background: var(--surface-card)">
          <div
            class="tw-pb-4 tw-border-b"
            style="
              padding-left: 1.5rem;
              padding-top: 1rem;
              border-bottom: 1px solid var(--surface-border);
              background: var(--surface-10);
            "
          >
            <div
              class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold"
              style="color: var(--text-color-secondary)"
            >
              <i class="pi pi-gift" style="color: var(--primary-color)"></i>
              <span
                >{{ selectedReferral.gifts.length }} Gift{{
                  selectedReferral.gifts.length !== 1 ? "s" : ""
                }}</span
              >
            </div>
          </div>

          <div class="tw-max-h-96 tw-overflow-y-auto gifts-scroll">
            <div
              v-for="(gift, _) in selectedReferral?.gifts || []"
              :key="gift.id"
              class="gift-card"
              :class="{ 'gift-card--verified': gift.is_gift_verified }"
            >
              <!-- Gift Status Badge -->
              <div class="tw-absolute tw-top-4 tw-right-4">
                <div
                  v-if="gift.is_gift_verified"
                  class="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-border"
                  style="
                    border-radius: var(--border-radius);
                    color: var(--primary-color);
                    border-color: var(--primary-color);
                  "
                >
                  <i class="pi pi-check-circle"></i>
                  <span>Approved</span>
                </div>
                <div
                  v-else
                  class="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-border"
                  style="
                    border-radius: var(--border-radius);
                    color: var(--orange-700);
                    border-color: var(--orange-500);
                  "
                >
                  <i class="pi pi-clock"></i>
                  <span>Pending</span>
                </div>
              </div>

              <!-- Gift Content -->
              <div class="tw-flex tw-items-start tw-gap-4 tw-mr-24">
                <div
                  class="tw-flex-shrink-0 tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xl tw-border-2"
                  style="
                    background: var(--surface-100);
                    border-color: var(--surface-200);
                  "
                >
                  <i
                    :class="getGiftIcon(gift.gift_type)"
                    :style="{
                      color: gift.is_gift_verified
                        ? 'var(--primary-color)'
                        : 'var(--surface-500)',
                    }"
                  ></i>
                </div>
                <div class="tw-flex-1 tw-min-w-0">
                  <h5
                    class="tw-m-0 tw-mb-2 tw-text-lg tw-font-semibold tw-leading-tight"
                    style="color: var(--text-color)"
                  >
                    {{ getGiftTypeDisplay(gift.gift_type) }}
                  </h5>
                  <p
                    class="tw-m-0 tw-mb-3 tw-text-sm tw-leading-relaxed"
                    style="color: var(--text-color-secondary)"
                  >
                    {{ getGiftDescription(gift.gift_type) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-3">
          <Button
            label="Close"
            icon="pi pi-times"
            @click="closeGiftsModal"
            class="p-button-outlined"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import Card from "primevue/card";
import Button from "primevue/button";
import { ref, computed, onBeforeMount } from "vue";
import { useStore } from "vuex";
import { FilterMatchMode } from "primevue/api";
import InputText from "primevue/inputtext";
import Dialog from "primevue/dialog";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { useToast } from "primevue/usetoast";

let tempFirst = 0;
const store = useStore();
const token = localStorage.getItem("podcasterToken");
const podcaster = computed(() => store.getters.podcaster || {});
const initFirst = ref(Number(localStorage.getItem("first")));
const showGiftsModal = ref(false);
const selectedReferral = ref(null);
const selectedReferralName = ref("");
const referralsTableFilter = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const toast = useToast();

const referrals = computed(() => {
  return store.getters.referrals || [];
});

// Enum for gift types
const GiftTypes = Object.freeze({
  REVSHARE_5_PERCENT_INCREASE: "revshare-5-percent-increase",
  AMAZON_GIFT_CARD: "amazon-gift-card",
});

// Function to handle page change
const onChangePage = (e) => {
  tempFirst = e.first;
  localStorage.removeItem("first");
};

// Function to fetch referrals
const fetchReferrals = async (isForce) => {
  if (isForce || !store.getters.referrals) {
    store.dispatch("loadReferrals", { token });
  }
};

// Function to format gift types
const getGiftTypeDisplay = (giftType) => {
  switch (giftType) {
    case GiftTypes.AMAZON_GIFT_CARD:
      return "Amazon Gift Card";
    case GiftTypes.REVSHARE_5_PERCENT_INCREASE:
      return "Revenue Share +5%";
    default:
      return "Unknown Gift";
  }
};

// Function to get gift descriptions
const getGiftDescription = (giftType) => {
  switch (giftType) {
    case GiftTypes.AMAZON_GIFT_CARD:
      return "A digital gift card for Amazon purchases";
    case GiftTypes.REVSHARE_5_PERCENT_INCREASE:
      return "5% increase in revenue sharing percentage";
    default:
      return "Gift details not available";
  }
};

// Function to get gift icons
const getGiftIcon = (giftType) => {
  switch (giftType) {
    case GiftTypes.AMAZON_GIFT_CARD:
      return "pi pi-shopping-cart";
    case GiftTypes.REVSHARE_5_PERCENT_INCREASE:
      return "pi pi-percentage";
    default:
      return "pi pi-gift";
  }
};

// Function to open gifts modal
const openGiftsModal = (referral) => {
  selectedReferral.value = referral;
  selectedReferralName.value = referral.name;
  showGiftsModal.value = true;
};

// Function to close gifts modal
const closeGiftsModal = () => {
  showGiftsModal.value = false;
  selectedReferral.value = null;
  selectedReferralName.value = "";
};

// Method to copy affiliate link to clipboard
const getAffiliateLink = (podcasterId) => {
  const link = "https://jamx.ai/podcasters-offer?ref_id=" + podcasterId;
  navigator.clipboard.writeText(link).then(
    function () {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Referral link copied to clipboard",
        life: 4000,
      });
    },
    function () {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to copy referral link to clipboard",
        life: 4000,
      });
    }
  );
};

onBeforeMount(() => {
  fetchReferrals();
});
</script>

<style scoped>
.p-datatable .p-datatable-thead > tr > th,
.p-datatable .p-datatable-tbody > tr > td {
  padding: 0.5rem;
  font-size: 0.9rem;
}

.p-card .p-card-body {
  padding: 0.5rem;
}

.p-dropdown,
.p-inputtext,
.p-button.p-button-sm {
  font-size: 0.9rem;
}

.p-button.p-button-sm {
  padding: 0.3rem 0.5rem;
}

.truncate-text {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.date-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.p-datatable .p-datatable-tbody > tr > td {
  max-height: 3.5em;
  overflow: hidden;
}

.gifts-modal :deep(.p-dialog-header) {
  background: var(--surface-card);
  border-bottom: 1px solid var(--surface-border);
  padding: 1.5rem;
}

.gifts-modal :deep(.p-dialog-content) {
  padding: 0;
  background: var(--surface-ground);
}

.gifts-modal-content {
  min-height: 200px;
}

.gift-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  margin-bottom: 0.75rem;
  padding: 1.25rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.gift-card:hover {
  box-shadow: var(--shadow-2);
  transform: translateY(-1px);
}

.gift-card--verified {
  border-left: 4px solid var(--primary-color);
}

.gift-card:last-child {
  margin-bottom: 0;
}

.gifts-scroll::-webkit-scrollbar {
  width: 6px;
}

.gifts-scroll::-webkit-scrollbar-track {
  background: var(--surface-100);
  border-radius: 3px;
}

.gifts-scroll::-webkit-scrollbar-thumb {
  background: var(--surface-300);
  border-radius: 3px;
}

.gifts-scroll::-webkit-scrollbar-thumb:hover {
  background: var(--surface-400);
}

@media (max-width: 768px) {
  .gifts-modal :deep(.p-dialog) {
    margin: 1rem;
    width: calc(100vw - 2rem) !important;
    max-width: none !important;
  }

  .gift-card .tw-mr-24 {
    margin-right: 0;
  }

  .gift-card .tw-flex {
    flex-direction: column;
    gap: 0.75rem;
  }

  .gift-card .tw-absolute {
    position: static;
    margin-bottom: 0.75rem;
    align-self: flex-end;
  }
}
</style>
